import React from 'react';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import { Tiempos, NationalBook } from 'fonts';
import { GenericSectionContent, MediaCard } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { sanitizeHtml, SECTION_SPACING, SectionCtas, textAlignmentClass } from '../../../helpers/rendering';

/**
 * Nexus ← Methven `section_with_columns`: a light-grey band of icon columns.
 *
 * It has two layouts, and the breakpoint between them is `lg` (1024), not `md`:
 *
 * - **lg and up** — three centred columns capped at 330px: icon, bold title, description.
 * - **below lg** — one full-width row per card: icon on the left, title centred in the space
 *   beside it, and NO description.
 *
 * Both come from `product-list/collection/collectioncards.tsx`, which is what the 8 /collection/*
 * pages render today; the geometry here is measured against staging at 1920, 1280, 1024, 768 and
 * 393. Dropping the description on the small layout looks like an oversight in that file and is
 * not — it is the live behaviour, and restoring it was the first thing reported wrong here.
 *
 * The 4 /our-technologies/* pages feed the same CMS block through a second V1 implementation (the
 * `section_with_columns` branch of `frontastic/tastics/content/our-technologies/index.tsx`), which
 * agrees on the band and the desktop columns but keeps a 3-up grid with descriptions all the way
 * down to md. Those pages opt into that layout per entry, with the class below.
 */

/**
 * Opt-in class on the section's `additional_css_class_names`: below lg, render the
 * /our-technologies/* layout instead of the /collection/* one -- a centred column per card, icon
 * capped at 150px, description shown, and the grid going 3-up from md rather than lg. Measured off
 * stage's /nz/our-technologies/airstream-technology/ at 390, 768, 1024 and 1440. It changes nothing
 * at lg and above, where the two V1 implementations already agree.
 */
const mobileDevicesToDisplayDescriptionAlso  = 'mobile-devices-to-display-description-also';

export default function NexusRendering({ content }: { content: GenericSectionContent }) {
  const { title, description, cards = [], content_alignment, ctas, additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'NexusRendering');
  const descriptionOnMobile = (additional_css_class_names ?? '').split(/\s+/).includes(mobileDevicesToDisplayDescriptionAlso);

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div
        className={`bg-[#F8F8F8] px-16 py-64 md:px-90 2xl:px-208 2xl:py-80 ${[
          additional_css_class_names,
          uniqueClass,
        ]
          .filter(Boolean)
          .join(' ')}`}
      >
        {title && (
          <Typography
            as="h2"
            className={`mb-24 text-28 font-medium leading-tight text-[#090909] xl:text-36 2xl:text-48 ${textAlignmentClass(
              content_alignment,
            )} ${Tiempos.className}`}
          >
            {title}
          </Typography>
        )}
        {description && (
          <div
            className={`mb-32 text-16 font-normal leading-6 text-[#333] 2xl:text-18 ${textAlignmentClass(
              content_alignment,
            )} ${NationalBook.className}`}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
          />
        )}
        {/* One full-width row per card until lg, three columns from lg. The row gap is V1's:
            30px, 20px from md, 47px from xl. With the opt-in class the grid is our-technologies'
            instead below lg: an 8px gap and 3-up from md, with the cards centred in their tracks. */}
        <div
          className={
            descriptionOnMobile
              ? 'grid grid-cols-1 justify-items-center gap-8 md:grid-cols-3 lg:gap-20 xl:gap-47'
              : 'grid grid-cols-1 gap-30 md:gap-20 lg:grid-cols-3 lg:justify-items-center xl:gap-47'
          }
        >
          {cards.map((card, index) => (
            <NexusCard key={index} card={card} descriptionOnMobile={descriptionOnMobile} />
          ))}
        </div>
        <SectionCtas ctas={ctas} className="mt-32 justify-center" />
      </div>
    </>
  );
}

function NexusCard({ card, descriptionOnMobile }: { card: MediaCard; descriptionOnMobile: boolean }) {
  // A per-card override only exists on the our-technologies side of V1 and nothing in the migrated
  // content sets it, so centred is the real default. `items-*` and `text-*` have to move together
  // or the icon and the copy disagree about which edge they sit on. Column layout only: below lg
  // the card is a row, where the icon leads and the title takes the rest.
  const alignment =
    card?.content_alignment === 'Left'
      ? 'lg:items-start lg:text-left'
      : card?.content_alignment === 'Right'
        ? 'lg:items-end lg:text-right'
        : 'lg:items-center lg:text-center';

  return (
    <div
      className={`flex w-full items-center text-center lg:max-w-[330px] lg:flex-col lg:justify-start ${
        descriptionOnMobile ? 'max-w-[330px] flex-col justify-start' : 'flex-row'
      } ${alignment}`}
    >
      {card?.desktop_image?.url && (
        <Image
          src={card.desktop_image.url}
          alt={card.desktop_image.description ?? card?.title ?? ''}
          width={180}
          height={180}
          // The icons are 180px-square SVG/PNG, so they are sized, never cropped: an explicit width
          // with `height: auto` keeps a non-square asset in proportion. `shrink-0` holds that width
          // in the row layout, where the title would otherwise squeeze it. our-technologies renders
          // the icon at 150 below lg, reaching 180 only on the desktop columns both layouts share.
          // V1 gets its 150 from `w-auto max-w-[150px]`; that can't be copied here, because this
          // `Image` asks Contentstack for an asset sized to the rendered width, so an auto width
          // feeds back on itself and settles at whatever variant loaded first (measured: 120px at
          // md, 150px at 390). The width has to be stated.
          style={{ height: 'auto' }}
          className={`block shrink-0 lg:mb-8 ${descriptionOnMobile ? 'mb-4 w-150 lg:w-180' : 'w-120 md:w-180'}`}
        />
      )}
      {/* No font class on the title on purpose. `next/font`'s className carries the face's own
          font-weight, which wins over `font-bold`; the page font is already National, so letting it
          inherit is what gives the 700 weight V1 renders.

          `m-auto` is what centres the title in the space beside the icon on the row layout; it has
          to be reset at lg, where the card is a stretched grid item and auto margins would push the
          title off the icon and away from its description. */}
      {card?.title && (
        <Typography
          as="h3"
          className={`text-20 font-bold leading-10 text-primary-black lg:m-0 lg:text-20 2xl:text-22 ${
            descriptionOnMobile ? '' : 'm-auto md:text-30'
          }`}
        >
          {card.title}
        </Typography>
      )}
      {/* Hidden below lg, as the live collection pages have it -- the row layout has no space for
          it beside the icon. The opt-in class is exactly the our-technologies pages saying they
          want it back, which their column layout has room for. */}
      {card?.description && (
        <div
          className={`text-16 font-normal leading-6 text-primary-black md:text-14 lg:block ${
            descriptionOnMobile ? '' : 'hidden'
          } ${NationalBook.className}`}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }}
        />
      )}
      {card?.ctas && card.ctas.length > 0 && <SectionCtas ctas={card.ctas} className="mt-16" />}
    </div>
  );
}
